/**
 * Murphy Door Configurator - Simplified Mode Coordinator
 *
 * PURPOSE: Orchestrates the 3-question simplified UX flow for the Flush Mount
 * Bookcase Door 2.0. After the user selects Wood & Finish, Width, and Door
 * Handing, this module triggers auto-application of all 17 remaining defaults
 * and publishes SIMPLIFIED_MODE_COMPLETE to show the confirmation panel.
 *
 * PHASE: 2 (Event-Driven CCP - Strict Enforcement)
 * MODULE TYPE: Business Logic (bl-)
 *
 * CCP COMPLIANCE:
 * - Bootstrap barrier: SYSTEM_MAPS_INITIALIZED
 * - Zero DOM access — category IDs and defaults sourced from PPD (ppd.simplifiedRequiredCategories,
 *   ppd.simplifiedDefaultsByCategory) populated from configurator_tab_categories metafields
 * - Zero module-to-module calls — pure event pub/sub
 * - Material resolution via conductor.getAppliedSelectionsMap() delegation
 * - simplifiedDefaultsPending flag for cascade-in-progress retry (no cross-module reads)
 * - simplifiedDefaultsTriggered guard prevents double-fire on unapply+reselect
 *
 * CONFIG SOURCE (data-driven, no hardcoding):
 * - Required categories: ppd.simplifiedRequiredCategories (from configurator_tab_categories.simplified_required)
 * - Default variants: ppd.simplifiedDefaultsByCategory (from configurator_tab_categories.simplified_default_variant_id)
 *   - Static default: "variantId" string
 *   - Material-aware: {material_aware:true, by_material_title:{label:stylePointerVariantId}, fallback:variantId}
 *     NOTE: by_material_title values are STYLE POINTERS (identify which product style — e.g. Standard vs
 *     Fluted casing — to default to for each material group). The KEYS are human-readable labels only and
 *     are NOT matched against product titles. resolveMaterialAwareDefault() looks up each style pointer's
 *     product in the PPD variantMap and finds the variant matching the user's selected wood_sku + finish_sku
 *     (e.g. KA+CS → "Knotty Alder Veneer / Clear Stain", PG+WH → "White / Paint Grade").
 * window.MurphyDoorConfigurator.simplifiedModeConfig only needs {enabled:true, productHandle:"..."}
 *
 * EVENT FLOW:
 * SYSTEM_URL_PARAM_DETECTED (module-load-time subscription)
 *   → set urlRestorationInProgress = true (suppresses simplified-mode counting)
 * SYSTEM_MAPS_INITIALIZED
 *   → init(): read config, publish BUSINESS_SIMPLIFIED_MODE_CATEGORIES_READY
 * SELECTION_APPLIED (filtered to simplified categories only)
 *   → if urlRestorationInProgress: skip (URL restore, not user interaction)
 *   → count 1/3, 2/3, 3/3 → at 3: publish BUSINESS_CASCADE_APPLY_SIMPLIFIED_DEFAULTS
 * BUSINESS_CASCADE_AUTO_SELECTION_COMPLETED (context === 'url_restoration')
 *   → clear urlRestorationInProgress, publish UI_TAB_OPEN_REQUEST { tabId: 'summary' }
 * BUSINESS_CASCADE_AUTO_SELECTION_COMPLETED (context !== 'simplified_mode')
 *   → if simplifiedDefaultsPending: prior cascade drained, retry fire
 * BUSINESS_CASCADE_AUTO_SELECTION_COMPLETED (context === 'simplified_mode')
 *   → push dataLayer, push Klaviyo, publish SIMPLIFIED_MODE_COMPLETE
 *
 * @version 1.0.0
 * @author Murphy Door Configurator Team
 */
(function (window) {
  'use strict';

  window.MurphyDoorConfigurator = window.MurphyDoorConfigurator || {};
  const CoreEventBus = window.MurphyDoorConfigurator.CoreEventBus;
  const CONSTANTS = window.MurphyDoorConfigurator.CONSTANTS;

  const isDev = () =>
    window.MurphyDoorConfigurator?.utils?.isDevelopment?.() ?? false;

  // ============================================================================
  // INITIALIZATION GUARD
  // ============================================================================
  let isInitialized = false;

  // ============================================================================
  // CLEANUP ARRAY
  // ============================================================================
  let cleanupHandlers = [];

  // ============================================================================
  // MODULE STATE
  // ============================================================================

  // Category names for the 3 user-facing simplified questions
  let simplifiedCategoryIds = new Set();

  // Tracks which simplified categories the user has completed
  const completedSimplifiedCategories = new Set();

  // True once we've fired BUSINESS_CASCADE_APPLY_SIMPLIFIED_DEFAULTS.
  // Prevents re-fire if the user unapplies and reselects a simplified category.
  let simplifiedDefaultsTriggered = false;

  // True while waiting for a prior cascade to drain before we can fire.
  // Pure event-driven retry — no cross-module cascadeInProgress reads.
  let simplifiedDefaultsPending = false;

  // Timestamp when simplified mode started (for GTM time_to_atc_seconds)
  let modeStartTime = null;

  // True while a ?config= URL restoration is in progress. Prevents the
  // simplified-mode counter from treating URL-restored selections as user
  // interaction and erroneously showing the "We built your door" panel.
  let urlRestorationInProgress = false;

  // ============================================================================
  // HELPERS
  // ============================================================================

  function pushDataLayer(eventName, payload) {
    // Staff traffic is never published (snippets/md-internal-traffic.liquid).
    if (window.MD_INTERNAL_TRAFFIC === true) return;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(Object.assign({ event: eventName }, payload));
  }

  // The md_element_click lane for these three milestones deliberately does NOT
  // live in this file. A hand-rolled publisher here shipped and was reverted:
  // it gated on `window.mdConsent.analyticsAllowed()` alone, which FAILS OPEN
  // by design while Shopify's Customer Privacy API is still initialising — so
  // all three facts transmitted unbuffered in exactly the pre-consent window
  // that after-the-fact consent does not cover.
  //
  // Instead this module publishes the FACT on the bus
  // (BUSINESS_SIMPLIFIED_MILESTONE_REACHED) and
  // md-config-support-analytics-reporter.js — the single place that owns
  // consent buffering, the four-field payload cap and the sample-rate knob —
  // decides whether and how it reaches the wire. That is also the CCP-correct
  // split: a BL module publishes a fact, a support module observes it. The
  // dataLayer lane below is independent and stays here unchanged.

  // Resolves the product handle from injected config. Returns 'unknown' if
  // simplifiedModeConfig is missing — telemetry stays consistent (never null),
  // and we never lose attribution when the config object is partially populated.
  function getProductHandle() {
    return (
      window.MurphyDoorConfigurator?.simplifiedModeConfig?.productHandle ||
      'unknown'
    );
  }

  // Resolves the correct composite-category variant for a material-aware simplified default.
  //
  // by_material_title values are STYLE POINTERS — they identify which product style to use
  // (e.g. Standard vs Fluted casing) for each material group. The keys are human-readable
  // labels only; matching is done entirely via PPD data, not title substring comparisons.
  //
  // Algorithm: for each style pointer's product, check whether it contains a variant
  // with the user's selected wood_sku + finish_sku. The first match wins. This correctly
  // handles all wood species (including all Veneer species) without relying on product
  // title strings that differ between the Wood & Finish product and composite product names.
  function resolveMaterialAwareDefault(defaultConfig, conductor) {
    if (!conductor || typeof conductor.getAppliedSelectionsMap !== 'function') {
      return defaultConfig.fallback || null;
    }
    const selections = conductor.getAppliedSelectionsMap();
    if (!selections) return defaultConfig.fallback || null;
    const woodFinishEntry = selections.get
      ? selections.get('Wood & Finish')
      : selections['Wood & Finish'];
    if (!woodFinishEntry || !woodFinishEntry.variantId)
      return defaultConfig.fallback || null;

    try {
      const ppd = conductor.getPerfectProductDictionary
        ? conductor.getPerfectProductDictionary()
        : null;
      if (ppd && ppd.variantMap) {
        const wfVariant = ppd.variantMap.get(Number(woodFinishEntry.variantId));
        if (wfVariant) {
          const selectedWoodSku =
            wfVariant.woods && wfVariant.woods[0] && wfVariant.woods[0].sku;
          const selectedFinishSku =
            wfVariant.finishes &&
            wfVariant.finishes[0] &&
            wfVariant.finishes[0].sku;
          if (selectedWoodSku && selectedFinishSku) {
            // Build productId → [{id, v}] index once to avoid O(S×V) scan
            const variantsByProduct = new Map();
            ppd.variantMap.forEach(function (v, id) {
              const pid = v.productId;
              if (!variantsByProduct.has(pid)) variantsByProduct.set(pid, []);
              variantsByProduct.get(pid).push({ id: id, v: v });
            });
            for (const [, stylePointerId] of Object.entries(
              defaultConfig.by_material_title || {}
            )) {
              if (!stylePointerId) continue;
              const styleVariant = ppd.variantMap.get(Number(stylePointerId));
              if (!styleVariant) continue;
              const candidates =
                variantsByProduct.get(styleVariant.productId) || [];
              for (var ci = 0; ci < candidates.length; ci++) {
                var cv = candidates[ci].v;
                if (
                  cv.woods &&
                  cv.woods[0] &&
                  cv.woods[0].sku === selectedWoodSku &&
                  cv.finishes &&
                  cv.finishes[0] &&
                  cv.finishes[0].sku === selectedFinishSku
                ) {
                  return String(candidates[ci].id);
                }
              }
            }
          }
        }
      }
    } catch (err) {
      isDev() &&
        console.error(
          '[SimplifiedModeCoordinator] resolveMaterialAwareDefault error:',
          err
        );
    }

    return defaultConfig.fallback || null;
  }

  function buildDefaultsPayload(conductor) {
    const ppd = conductor.getPerfectProductDictionary
      ? conductor.getPerfectProductDictionary()
      : null;
    if (!ppd || !ppd.simplifiedDefaultsByCategory) {
      isDev() &&
        console.warn(
          '[SimplifiedModeCoordinator] No simplifiedDefaultsByCategory in PPD — no defaults to apply'
        );
      return [];
    }

    // C2: the 3 curated-required categories may now carry a
    // simplified_default_variant_id (needed by the curated load-time defaults).
    // Simplified mode must NOT bulk-apply those — the user answers them — so
    // skip anything in ppd.simplifiedRequiredCategories here.
    const requiredSet = new Set(
      Array.isArray(ppd.simplifiedRequiredCategories)
        ? ppd.simplifiedRequiredCategories
        : []
    );

    const selections = [];
    ppd.simplifiedDefaultsByCategory.forEach((defaultConfig, categoryId) => {
      if (defaultConfig === null || defaultConfig === undefined) return;
      if (requiredSet.has(categoryId)) return;

      let variantId;
      if (typeof defaultConfig === 'object' && defaultConfig.material_aware) {
        // Material-aware: resolve variant by matching current Wood & Finish selection title
        variantId = resolveMaterialAwareDefault(defaultConfig, conductor);
      } else {
        // Static default: plain variant ID string
        variantId =
          typeof defaultConfig === 'string'
            ? defaultConfig
            : String(defaultConfig);
      }

      if (variantId) {
        selections.push({ categoryId, variantId });
      }
    });

    return selections;
  }

  function fireSimplifiedDefaults(conductor) {
    const defaults = buildDefaultsPayload(conductor);
    isDev() &&
      console.log(
        '[SimplifiedModeCoordinator] Firing simplified defaults:',
        defaults.length,
        'selections'
      );
    CoreEventBus.publish(
      CONSTANTS.EVENTS.BUSINESS_CASCADE_APPLY_SIMPLIFIED_DEFAULTS,
      {
        selections: defaults,
        context: 'simplified_mode',
      }
    );
  }

  // ============================================================================
  // EVENT HANDLERS
  // ============================================================================

  function handleSystemMapsInitialized(eventData, conductor) {
    try {
      const config = window.MurphyDoorConfigurator.simplifiedModeConfig;
      if (!config || !config.enabled) {
        isDev() &&
          console.log(
            '[SimplifiedModeCoordinator] Simplified mode not enabled in config'
          );
        return;
      }

      // Primary source: PPD from configurator_tab_categories metafields (data-driven)
      const ppd = conductor.getPerfectProductDictionary
        ? conductor.getPerfectProductDictionary()
        : null;
      const categoryList =
        ppd &&
        ppd.simplifiedRequiredCategories &&
        ppd.simplifiedRequiredCategories.length > 0
          ? ppd.simplifiedRequiredCategories
          : null;

      if (!categoryList || categoryList.length === 0) {
        isDev() &&
          console.warn(
            '[SimplifiedModeCoordinator] No simplified required categories found in PPD (check configurator_tab_categories.simplified_required metafield)'
          );
        return;
      }

      simplifiedCategoryIds = new Set(categoryList);
      modeStartTime = Date.now();

      isDev() &&
        console.log(
          '[SimplifiedModeCoordinator] Simplified mode active. Categories from PPD:',
          [...simplifiedCategoryIds]
        );

      CoreEventBus.publish(
        CONSTANTS.EVENTS.BUSINESS_SIMPLIFIED_MODE_CATEGORIES_READY,
        {
          categoryIds: categoryList,
        }
      );

      pushDataLayer('simplified_mode_started', {
        product_handle: getProductHandle(),
        category_count: categoryList.length,
      });
      CoreEventBus.publish(
        CONSTANTS.EVENTS.BUSINESS_SIMPLIFIED_MILESTONE_REACHED,
        {
          milestone: 'started',
          categoryId: null,
          timestamp: Date.now(),
        }
      );
    } catch (error) {
      isDev() &&
        console.error(
          '[SimplifiedModeCoordinator] handleSystemMapsInitialized error:',
          error
        );
    }
  }

  function handleCategoryCompleted(eventData, conductor) {
    try {
      const detail = eventData.detail || eventData;
      const categoryId = detail.categoryId;

      if (urlRestorationInProgress) return;
      if (!simplifiedCategoryIds.has(categoryId)) return;
      if (simplifiedDefaultsTriggered) return;

      completedSimplifiedCategories.add(categoryId);

      isDev() &&
        console.log(
          '[SimplifiedModeCoordinator] Simplified category completed: "' +
            categoryId +
            '" (' +
            completedSimplifiedCategories.size +
            '/' +
            simplifiedCategoryIds.size +
            ')'
        );

      pushDataLayer('simplified_step_completed', {
        step: completedSimplifiedCategories.size,
        category: categoryId,
        value: detail.selectedTitle || '',
        product_handle: getProductHandle(),
      });
      // categoryId is an id, never detail.selectedTitle -- titles are free text
      // and out of scope for the analytics channel this fact feeds.
      CoreEventBus.publish(
        CONSTANTS.EVENTS.BUSINESS_SIMPLIFIED_MILESTONE_REACHED,
        {
          milestone: 'step_completed',
          categoryId: categoryId,
          timestamp: Date.now(),
        }
      );

      if (completedSimplifiedCategories.size >= simplifiedCategoryIds.size) {
        simplifiedDefaultsTriggered = true;
        simplifiedDefaultsPending = true;
        fireSimplifiedDefaults(conductor);
      }
    } catch (error) {
      isDev() &&
        console.error(
          '[SimplifiedModeCoordinator] handleCategoryCompleted error:',
          error
        );
    }
  }

  function handleCascadeCompleted(eventData, conductor) {
    try {
      const detail = eventData.detail || eventData;
      const context = detail.context;

      // URL restoration complete: transition the simplified-mode DOM state and
      // land user on Summary tab instead of showing "We built your door" panel.
      // We publish SIMPLIFIED_MODE_COMPLETE (source: url_restoration) so the
      // navigation manager clears its category allowlist and unhides all tabs.
      // CTA engagement checks source and skips the panel for url_restoration.
      if (context === 'url_restoration') {
        urlRestorationInProgress = false;
        isDev() &&
          console.log(
            '[SimplifiedModeCoordinator] URL restoration complete — transitioning to Summary tab'
          );
        CoreEventBus.publish(
          CONSTANTS.EVENTS.BUSINESS_SIMPLIFIED_MODE_COMPLETE,
          {
            productHandle:
              window.MurphyDoorConfigurator.simplifiedModeConfig
                ?.productHandle || '',
            timeToCompleteMs: 0,
            source: 'url_restoration',
          }
        );
        CoreEventBus.publish(CONSTANTS.EVENTS.UI_TAB_OPEN_REQUEST, {
          tabId: 'summary',
          source: 'url_restoration',
          reason: 'config_url_complete',
        });
        return;
      }

      if (!simplifiedDefaultsPending) return;

      if (context !== 'simplified_mode') {
        // A different cascade drained — retry firing simplified defaults
        isDev() &&
          console.log(
            '[SimplifiedModeCoordinator] Prior cascade drained (context: ' +
              context +
              '), retrying simplified defaults'
          );
        fireSimplifiedDefaults(conductor);
        return;
      }

      // Our cascade completed
      simplifiedDefaultsPending = false;

      isDev() &&
        console.log(
          '[SimplifiedModeCoordinator] Simplified defaults applied. Publishing SIMPLIFIED_MODE_COMPLETE.'
        );

      const timeToCompleteMs = modeStartTime ? Date.now() - modeStartTime : 0;

      const productHandle = getProductHandle();

      // GTM event — no ecommerce block to avoid duplicate Meta/GA4/TikTok attribution
      pushDataLayer('simplified_auto_applied', {
        product_handle: productHandle,
        time_to_atc_seconds: parseFloat((timeToCompleteMs / 1000).toFixed(1)),
      });
      // The flow's terminal milestone. It carries no categoryId -- it is about
      // the whole simplified run, not one step.
      CoreEventBus.publish(
        CONSTANTS.EVENTS.BUSINESS_SIMPLIFIED_MILESTONE_REACHED,
        {
          milestone: 'auto_applied',
          categoryId: null,
          timestamp: Date.now(),
        }
      );

      // Klaviyo: pre-ATC abandonment segmentation (fires before user clicks ATC).
      // Gated on marketing consent -- this is a marketing event tied to a product
      // handle, not first-party analytics. mdConsent fails OPEN, so if the helper
      // has not loaded this behaves exactly as it did before.
      try {
        // Staff traffic is never published (snippets/md-internal-traffic.liquid).
        if (
          window.MD_INTERNAL_TRAFFIC !== true &&
          typeof window._learnq !== 'undefined' &&
          (!window.mdConsent || window.mdConsent.marketingAllowed())
        ) {
          window._learnq.push([
            'track',
            'Simplified Mode Auto Applied',
            {
              'Product Handle': productHandle,
            },
          ]);
        }
      } catch (_) {}

      CoreEventBus.publish(CONSTANTS.EVENTS.BUSINESS_SIMPLIFIED_MODE_COMPLETE, {
        productHandle: productHandle,
        timeToCompleteMs: timeToCompleteMs,
      });

      // Funnel-mode celebration cascade — staggered so the user can VISUALLY
      // register each beat in the right ORDER. The natural beat is:
      //   progress bar fills to 100% (culmination) → confetti pops (reward)
      //   → Summary tab arrives (handoff to checkout).
      //
      // ui-updater.updateProgressBar() defers its width write inside
      // setTimeout(800ms) (so the --celebrating bounce can play first), then
      // the CSS transition `width 0.3s ease` runs. That means the bar
      // actually hits 100% around STATE_PROGRESS_UPDATED + ~1100ms. Firing
      // confetti before then makes the celebration feel like it's happening
      // DURING the fill instead of AS the reward for completing it.
      //
      // Timings:
      //
      // T+0ms     UI_CTA_ENGAGED — fullscreen guarantee (idempotent; the
      //           auto-expand of the first simplified category already
      //           triggered it via SELECTION_BROWSING, but publishing here
      //           defends against ordering changes upstream).
      // T+1200ms  UI_CONFETTI_CELEBRATION_REQUESTED — fires ~100ms AFTER the
      //           progress bar has finished filling to 100%, so the
      //           celebration lands on the completed bar rather than racing
      //           it.
      // T+2700ms  UI_TAB_OPEN_REQUEST tabId='summary' — 1.5s after confetti
      //           starts, giving the user time to register the celebration
      //           moment before Summary takes over. The confetti canvas is
      //           a fixed-position z-index 9999 overlay, so particles
      //           continue playing OVER the Summary tab once it arrives —
      //           bonus continuity.
      CoreEventBus.publish(CONSTANTS.EVENTS.UI_CTA_ENGAGED, {
        trigger: 'simplified_mode_complete',
        timestamp: Date.now(),
      });

      // Claim the conductor's 100%-completion confetti slot SYNCHRONOUSLY
      // so the generic "configurator hit 100%" path in
      // md-config-core-conductor.js → handleProgressUpdate doesn't fire a
      // SECOND confetti blast behind our staggered one.
      //
      // Why this is needed: the cascade applies all 17 defaults, each
      // STATE_PROGRESS_UPDATED reaches updateProgressBar(), and the last
      // one (progress = 100) schedules a setTimeout(0) that publishes
      // UI_PROGRESS_CELEBRATION_TRIGGERED. The conductor subscribes to
      // that event and calls handleProgressUpdate(100), which publishes
      // its OWN UI_CONFETTI_CELEBRATION_REQUESTED with
      // trigger: 'configurator_complete' (md-config-core-conductor.js
      // lines 5975-5983), guarded by its own _celebrationFired flag.
      // That flag protects the conductor from its own duplicate publish
      // but knows nothing about our staggered T+1200ms publish. Result:
      // user sees two confetti blasts ~1.2s apart.
      //
      // Diagnosed by ccp-cascade-logic-auditor 2026-06-18.
      //
      // Fix: pre-claim the conductor's flag now, while we're still in
      // the synchronous BUSINESS_CASCADE_AUTO_SELECTION_COMPLETED handler
      // call stack. The setTimeout(0) that triggers handleProgressUpdate
      // is queued but hasn't fired yet — when it does fire, the
      // _celebrationFired guard will see true and take the "skipping
      // duplicate" branch. Our deliberate T+1200ms publish below is the
      // only confetti event that reaches the manager.
      //
      // Standard-mode (non-simplified) configurator users are unaffected:
      // they still get the conductor's 100% confetti because nobody
      // pre-claims the flag in their flow.
      const conductor =
        window.MurphyDoorConfigurator?.main ||
        window.MurphyDoorConfigurator?.conductor;
      if (conductor && typeof conductor.claimCelebrationSlot === 'function') {
        // CCP delegation (Rule 2): claim the slot via the conductor's own
        // method rather than reaching in and mutating `_celebrationFired`.
        conductor.claimCelebrationSlot();
      } else {
        isDev() &&
          console.error(
            '[SimplifiedModeCoordinator] CRITICAL: conductor.claimCelebrationSlot unavailable — double-confetti pre-claim failed; two confetti blasts may fire.'
          );
      }

      setTimeout(function () {
        CoreEventBus.publish(
          CONSTANTS.EVENTS.UI_CONFETTI_CELEBRATION_REQUESTED,
          {
            trigger: 'simplified_mode_complete',
            particleCount: 150,
          }
        );
      }, 1200);
      setTimeout(function () {
        CoreEventBus.publish(CONSTANTS.EVENTS.UI_TAB_OPEN_REQUEST, {
          tabId: 'summary',
          source: 'simplified_mode_complete',
          reason: 'funnel_complete',
        });
      }, 2700);
    } catch (error) {
      isDev() &&
        console.error(
          '[SimplifiedModeCoordinator] handleCascadeCompleted error:',
          error
        );
    }
  }

  // ============================================================================
  // INITIALIZE
  // ============================================================================

  function initialize(conductor, eventBus) {
    if (isInitialized) {
      isDev() &&
        console.warn('[SimplifiedModeCoordinator] Already initialized');
      return false;
    }

    const bus = eventBus || CoreEventBus;
    if (!bus) {
      isDev() &&
        console.error(
          '[SimplifiedModeCoordinator] Cannot initialize: EventBus not available'
        );
      return false;
    }

    if (!conductor) {
      isDev() &&
        console.error(
          '[SimplifiedModeCoordinator] Cannot initialize: conductor not provided'
        );
      return false;
    }

    try {
      // Run setup immediately — conductor calls initialize() AFTER SYSTEM_MAPS_INITIALIZED
      // has already fired, so we execute directly rather than subscribing and waiting.
      handleSystemMapsInitialized({}, conductor);

      // Track user completions of the simplified categories.
      // NOTE: STATE_CATEGORY_COMPLETED is never published for normal applied selections because
      // handleApplySelection() pre-stamps completion state BEFORE COMPLETION_UPDATE fires,
      // making previousState === newState. Use SELECTION_APPLIED instead — it fires reliably
      // on every confirmed selection with categoryId in the payload.
      const unsubCategoryCompleted = bus.subscribe(
        CONSTANTS.EVENTS.SELECTION_APPLIED,
        function (eventData) {
          handleCategoryCompleted(eventData, conductor);
        }
      );
      cleanupHandlers.push(unsubCategoryCompleted);

      // Detect when any cascade completes — either retry or confirm simplified_mode completion
      const unsubCascadeCompleted = bus.subscribe(
        CONSTANTS.EVENTS.BUSINESS_CASCADE_AUTO_SELECTION_COMPLETED,
        function (eventData) {
          handleCascadeCompleted(eventData, conductor);
        }
      );
      cleanupHandlers.push(unsubCascadeCompleted);

      isInitialized = true;
      isDev() && console.log('[SimplifiedModeCoordinator] Initialized');
      return true;
    } catch (error) {
      isDev() &&
        console.error(
          '[SimplifiedModeCoordinator] Initialization failed:',
          error
        );
      return false;
    }
  }

  // ============================================================================
  // CLEANUP
  // ============================================================================

  function cleanup() {
    cleanupHandlers.forEach(function (unsub) {
      try {
        unsub();
      } catch (_) {}
    });
    cleanupHandlers = [];
    isInitialized = false;
    urlRestorationInProgress = false;
  }

  // ============================================================================
  // PUBLIC API
  // ============================================================================

  window.MurphyDoorConfigurator.simplifiedModeCoordinator = {
    initialize: initialize,
    cleanup: cleanup,
  };

  // ============================================================================
  // MODULE-LOAD-TIME: URL RESTORATION DETECTION
  // Subscribe at load time (before conductor initializes this module) so the
  // flag is set before the deferred BUSINESS_CASCADE_APPLY_URL_SELECTIONS fires.
  // Failure events are also handled here so the flag can never get stuck.
  // ============================================================================
  if (CoreEventBus) {
    const unsubUrlDetected = CoreEventBus.subscribe(
      CONSTANTS.EVENTS.SYSTEM_URL_PARAM_DETECTED,
      function () {
        urlRestorationInProgress = true;
        isDev() &&
          console.log(
            '[SimplifiedModeCoordinator] URL param detected — suppressing simplified-mode counting during restoration'
          );
      }
    );
    cleanupHandlers.push(unsubUrlDetected);

    // Reset flag on any URL parse/validation failure so simplified mode
    // remains usable for users who land with a broken config link.
    const clearOnFailure = function () {
      if (urlRestorationInProgress) {
        urlRestorationInProgress = false;
        isDev() &&
          console.log(
            '[SimplifiedModeCoordinator] URL restoration failed — re-enabling simplified-mode counting'
          );
      }
    };
    cleanupHandlers.push(
      CoreEventBus.subscribe(
        CONSTANTS.EVENTS.SYSTEM_URL_PARAM_PARSE_FAILED,
        clearOnFailure
      )
    );
    cleanupHandlers.push(
      CoreEventBus.subscribe(
        CONSTANTS.EVENTS.SYSTEM_URL_PARAM_VALIDATION_FAILED,
        clearOnFailure
      )
    );
    cleanupHandlers.push(
      CoreEventBus.subscribe(
        CONSTANTS.EVENTS.BUSINESS_CASCADE_AUTO_SELECTION_FAILED,
        function (eventData) {
          const context =
            (eventData && eventData.context) ||
            (eventData && eventData.detail && eventData.detail.context);
          if (context === 'url_restoration') clearOnFailure();
        }
      )
    );
  }
})(window);
